import React, { FC, Fragment, useContext } from 'react';
import { ContextApi } from 'react-uforms';
import { Text } from '@ch/common/components/text';
import { IconCheckFilled } from '@ch/common/components/icon';
import { ColorProps } from '@ch/common/const/color';
import { useData } from '../use-data';
import { useQuiz } from './hooks/use-quiz';
import {
  QuizGroupNavStyled,
  QuizGroupNavItemStyled,
  QuizGroupNavLink,
  QuizGroupNavItemIcon,
  QuizGroupNavItemCompleted,
  QuizNavWrap,
  QuizNavContainer,
} from './quiz.styled';
import { desktopIconProps, mobileIconProps, subFieldGroupsData, titleProps } from './const';
import { fieldGroups } from './types';
import { QuizContext } from './quiz-context';
import { GridBreakpointsMax } from '@ch/common/const/grid';
import useMediaQuery from './hooks/use-media-query';
import { IconProps } from '@ch/common/components/icon/types';
import { Col, Row } from 'styled-bootstrap-grid';
import { useLang } from '../use-lang';

const QuizGroupNavItem: FC<{
  icon: any;
  label: string;
  activeParent?: boolean;
  $active?: boolean;
  $completed?: boolean;
  $width: number;
  href?: string;
}> = ({ icon, label, activeParent, $active, $completed, $width, href }) => {
  const isDesktop = useMediaQuery(`(min-width: ${+GridBreakpointsMax?.lg + 1}px)`);
  const { lang } = useLang();
  const Icon = icon;
  const state: { $color?: ColorProps } = {
    ...($completed && { $color: 'success' }),
    ...($active && { $color: 'primary' }),
  };
  const linkProps = {
    ...(href && activeParent && { href }),
  };

  const iconProps: IconProps = {
    ...desktopIconProps,
    $size: isDesktop ? desktopIconProps?.$size : mobileIconProps?.$size,
  };

  return (
    <QuizGroupNavItemStyled {...{ $width, $active, $completed }}>
      <QuizGroupNavLink {...linkProps} as={href && activeParent ? 'a' : 'span'}>
        <QuizGroupNavItemIcon>
          <Icon {...{ ...iconProps, ...state }} />
          {$completed && !$active && (
            <QuizGroupNavItemCompleted
              {...{
                $display: 'flex',
                $alignItems: 'center',
                $justifyContent: 'center',
              }}
            >
              <IconCheckFilled {...{ $size: isDesktop ? 14 : 9, $color: 'success' }} />
            </QuizGroupNavItemCompleted>
          )}
        </QuizGroupNavItemIcon>
        <Text
          $display={{ xs: 'none', lg: 'block' }}
          {...{ ...titleProps, $size: `${lang === 'fr' || lang === 'ja' ? 'xs' : 'sm'}`, ...state }}
        >
          {label}
        </Text>
      </QuizGroupNavLink>
    </QuizGroupNavItemStyled>
  );
};

const QuizNav: FC = () => {
  const {
    data: { questions, keywords },
  } = useData();
  const { steps } = useQuiz();
  const { submitted } = useContext(QuizContext);
  const { getGroup } = useContext(ContextApi);

  const subNav = questions
    ?.filter(el => el?.field_type === 'label')
    ?.reduce((result, { question, identifier }) => {
      const icon = subFieldGroupsData[identifier];
      return {
        ...result,
        ...(identifier && question && icon && { [identifier]: { id: identifier, icon, label: question } }),
      };
    }, {});

  const stepsCount = steps && Object.keys(steps).length ? Object.keys(steps).length : 1;
  const subStepsCount = subNav && Object.keys(subNav).length ? Object.keys(subNav).length : 1;
  const $width = 100 / (stepsCount - 1 + subStepsCount);

  steps[fieldGroups.FORM].subNav = subNav;

  return (
    <QuizNavWrap>
      <QuizNavContainer>
        <Row justifyContent="center">
          <Col xs={12} sm={10} lg={12}>
            <QuizGroupNavStyled>
              {Object.keys(steps).map(key => {
                const { subNav, icon } = steps[key];
                const localizedLabel = keywords[`${key}StepLabel`];
                const group = getGroup(key);
                return subNav ? (
                  <Fragment key={key}>
                    {Object.values(subNav).map(({ id, icon, label }) => (
                      <QuizGroupNavItem
                        key={id}
                        {...{
                          icon,
                          label,
                          activeParent: group?.isActive,
                          href: `#${id}`,
                          $active: false,
                          $completed: submitted,
                          $width,
                        }}
                      />
                    ))}
                  </Fragment>
                ) : (
                  <QuizGroupNavItem
                    key={key}
                    {...{
                      icon,
                      label: localizedLabel,
                      $active: group?.isActive,
                      $completed: submitted || group?.isCompleted,
                      $width,
                    }}
                  />
                );
              })}
            </QuizGroupNavStyled>
          </Col>
        </Row>
      </QuizNavContainer>
    </QuizNavWrap>
  );
};

export default QuizNav;
